import NextImage from '@/components/cms/image';
import RichText from '@/components/cms/rich-text';
import Link from '@/components/link';
import WPTypography from '@/components/typography';
import { CompanyBlogPostContainer } from '@/pages/company/components/styled-components';
import { Box, Container, useTheme } from '@drivekyte/web-components';
import { useMedia } from '@drivekyte/ui';
import dayjs from 'dayjs';
import Head from 'next/head';
import MorePosts from '../more-posts';
import { CompanyBlogPostPageProps } from './types';
import { SearchbarToggleProvider } from '@/pages/home-page/components/nav-bar/desktop-nav-bar-search/search-bar-context';
import NavBar from '@/pages/home-page/components/nav-bar/nav-bar';
import {
  getMetaTagsFromMetaData,
  getNavBarData,
} from '@/utils/strapi-cms/cms-utils';
import Seo from '@/components/cms/seo/seo';

const CompanyBlogPostPage = (props: CompanyBlogPostPageProps) => {
  const data = props?.data;

  const [theme] = useTheme();
  const media = useMedia();
  const isMobile = media.xs;

  const navBarData = getNavBarData(data?.navigationBar);
  const metaTags = getMetaTagsFromMetaData(data.meta);

  const renderAuthor = () => (
    <Box
      display="flex"
      paddingVertical="small"
      marginBottom={isMobile ? undefined : 'xLarge'}
    >
      <NextImage
        media={data?.author?.avatar}
        borderRadius="50%"
        width={56}
        height={56}
        objectFit="cover"
        priority={true}
      />
      <Box display="flex" flexDirection="column" paddingLeft="small">
        <WPTypography
          variant="span"
          color="primary"
          fontSize={16}
          lineHeight={24}
          fontWeight={500}
        >
          {data?.author?.name}
        </WPTypography>
        <WPTypography
          variant="span"
          color="primary"
          fontSize={16}
          lineHeight={24}
          fontWeight={400}
        >
          {data?.date ? dayjs(data.date).format('MMM D, YYYY') : null}
        </WPTypography>
      </Box>
    </Box>
  );

  return (
    <>
      <Head>
        <title>{data?.title || 'Company Blog Post'} | Kyte Blog</title>
      </Head>
      <Seo meta={metaTags} />
      <SearchbarToggleProvider
        isStickySearchBar={navBarData.fixedSearchOnScroll}
      >
        <NavBar navState={navBarData.variant} color={navBarData.color} />
      </SearchbarToggleProvider>
      <CompanyBlogPostContainer
        mobilePadding={theme.spacings.small}
        webPadding={theme.spacings.xxxLarge}
      >
        <Container maxWidth="md">
          <Box
            marginTop="xxxxLarge"
            marginBottom={isMobile ? 'small' : 'medium'}
          >
            <Link
              href="/company"
              color="$backgroundPositive"
              variant={'labelMedium'}
            >
              Company Blog
            </Link>
          </Box>

          {isMobile && renderAuthor()}

          <Box marginBottom={isMobile ? 'medium' : 'small'}>
            <WPTypography
              variant="h1"
              color="primary"
              fontFamily="Gilroy"
              fontSize={48}
              lineHeight={64}
              fontWeight={700}
            >
              {data?.title}
            </WPTypography>
          </Box>

          {!isMobile && renderAuthor()}

          <Box
            width={isMobile ? '320px' : '1024px'}
            height={isMobile ? '191px' : '667px'}
            position="relative"
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <NextImage
              media={data?.headlineImage}
              borderRadius="8px"
              fill={true}
              priority={true}
            />
          </Box>

          <Box paddingHorizontal={isMobile ? undefined : 'medium'}>
            <RichText data={{ theme: 'white', content: data?.content }} />
          </Box>

          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            paddingLeft={isMobile ? 'small' : 'xxLarge'}
            marginTop={isMobile ? 'small' : 'xxxLarge'}
            marginBottom={isMobile ? 'xxLarge' : 'xxxxLarge'}
          >
            <NextImage
              media={data?.author?.avatar}
              borderRadius="50%"
              width={56}
              height={56}
              objectFit="cover"
            />
            <Box
              display="flex"
              paddingLeft={isMobile ? undefined : 'small'}
              paddingRight={isMobile ? undefined : 'medium'}
              marginTop={isMobile ? 'tiny' : undefined}
            >
              <WPTypography
                variant="span"
                color="primary"
                fontSize={16}
                lineHeight={24}
                fontWeight={100}
              >
                {data?.author?.description}
              </WPTypography>
            </Box>
          </Box>

          <MorePosts
            moreBlogPostsHeader={data?.moreBlogPostsHeader}
            posts={data?.moreBlogPosts?.data}
          />
        </Container>
      </CompanyBlogPostContainer>
    </>
  );
};

export default CompanyBlogPostPage;
